import { useId } from 'react';
import { updateWithToast, updateWithToastPromise } from '@/clientOnlyUtils/clientUtils';
import { toast } from 'react-hot-toast';

import { useAddContactTagMutation, useRemoveContactTagMutation, useTagCreateMutation } from '@/hooks/trpc/useTagMutation';

type Tag = { id: string; name: string; contactOnly: boolean };
import { api } from '@/utils/api';
import { BadgeWButton } from '@poseidon/components/Input2';
import { Button, FilterPopover, Label } from '@poseidon/components/Input2';
import { cleanId } from '@poseidon/components/Input2/types';
import { General } from '@poseidon/components/StrokeIcons';

interface ContactTagEditorProps {
  contactId: string;
  teamId: string;
}

export const ContactTagEditor = ({ contactId, teamId }: ContactTagEditorProps) => {
  const id = useId();
  const anchorName = `--contact-tags-${cleanId(id)}`;
  const popoverId = `contact-tags-popover-${cleanId(id)}`;

  const contactTagsQuery = api.tag.contactTags.useQuery({ contactId });
  const allTagsQuery = api.tag.all.useQuery({ teamId });

  const addTagMutation = useAddContactTagMutation();
  const removeTagMutation = useRemoveContactTagMutation();
  const createTagMutation = useTagCreateMutation();

  const contactTags = contactTagsQuery.data?.tags ?? [];
  const allTags = allTagsQuery.data?.tags ?? [];

  const assignedTagIds = new Set(contactTags.map((t) => t.id));
  const availableOptions = allTags
    .filter((t) => !assignedTagIds.has(t.id) && t.contactOnly)
    .map((t) => ({ value: t.id, label: t.name }));

  const onAddTag = async (tagId: string) => {
    const tag = allTags.find((t) => t.id === tagId);
    if (!tag) return;

    try {
      await updateWithToast({
        loading: 'Adding tag...',
        success: `Tag "${tag.name}" added`,
        mutationFnAsync: () => addTagMutation.mutateAsync({ contactId, tagId }),
      });
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const onRemoveTag = async (tag: Tag) => {
    try {
      await updateWithToast({
        loading: 'Removing tag...',
        success: `Tag "${tag.name}" removed`,
        mutationFnAsync: () => removeTagMutation.mutateAsync({ contactId, tagId: tag.id }),
      });
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const onCreateTag = async (name: string) => {
    try {
      const newTag = await updateWithToastPromise({
        loading: 'Creating tag...',
        success: `Tag "${name}" created and added`,
        mutationFnAsync: () => createTagMutation.mutateAsync({ teamId, data: { name, contactOnly: true } }),
      });
      await addTagMutation.mutateAsync({ contactId, tagId: newTag.id });
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const tagsLength = contactTags.length;

  return (
    <div className="flex flex-col gap-2">
      <Label>Tags</Label>
      <div className="flex flex-wrap items-center gap-2">
        {contactTags.map((tag) => (
          <BadgeWButton key={tag.id} onButtonClick={() => onRemoveTag(tag)} bIcon={<General.Trash01 />} buttonClassName="hover:text-red-500! hover:bg-red-50! dark:hover:bg-red-950!">
            {tag.name}
          </BadgeWButton>
        ))}
        <Button
          popoverTarget={popoverId}
          icon={<General.PlusCircle />}
          disabled={allTagsQuery.isPending}
          size="sm"
          text={tagsLength > 0}
          style={{ anchorName } as React.CSSProperties}>
          {tagsLength > 0 ? null : 'Add Tag'}
        </Button>
        <FilterPopover
          popoverId={popoverId}
          anchorName={anchorName}
          size="sm"
          items={availableOptions}
          onSelect={onAddTag}
          onCreate={onCreateTag}
          popoverStyle={{ minWidth: '150px' }}
        />
      </div>
    </div>
  );
};